import "./NewsWidgetComponent.scss";
import template from "./NewsWidgetComponent.html";
import {IComponentOptions, IOnDestroy, IOnInit} from "angular";
import {BehaviorSubject, Observable, ReplaySubject, Subject} from "rxjs";
import {ComponentCallback} from "../../../../../types/AngularJS";
import {isNotNull} from "../../../../../js/util/UtilFunctions";
import {NewsWidgetConfig} from "./models/NewsWidgetConfig";
import {CodeAndExchange, Securities} from "../../../../../instruments/types/CodeAndExchange";
import {SelectedNewsSources} from "../../../../../news/components/news-source-selector/model/SelectedNewsSources";
import {
    isSecurityChange,
    isSourcesChange,
    NewsWidgetConfigChange,
    SecurityChange,
    SourcesChange
} from "./models/NewsWidgetConfigChange";
import {WidgetDisplayMode} from "../../../models/WidgetDisplayMode";

export const NewsWidget = "tfNewsWidget";

export function newsWidget(): IComponentOptions {
    return {
        template,
        bindings: {
            security: "<",
            config: "<",
            displayMode: "<",
            onSecurityChanged: "&",
            onConfigChanged: "&"
        },
        controllerAs: "ctrl",
        controller: NewsWidgetComponentController
    };
}

export class NewsWidgetComponentController implements IOnInit, IOnDestroy {

    /** @input */
    public set security(security: CodeAndExchange | null) {
        if (security != null) {
            const change = SecurityChange(security);
            this.configChanges$.next(change);
        }
    }

    /** @input */
    public set config(config: NewsWidgetConfig | null) {
        if (config != null) {
            this.config$.next(config);
        }
    }

    private readonly config$ = new BehaviorSubject<NewsWidgetConfig | null>(null);

    /** @input */
    public set displayMode(displayMode: WidgetDisplayMode | null) {
        if (displayMode != null) {
            this.displayMode$.next(displayMode);
        }
    }

    private readonly displayMode$ = new ReplaySubject<WidgetDisplayMode>(1);

    /** @output */
    public readonly onSecurityChanged: ComponentCallback;

    /** @output */
    public readonly onConfigChanged: ComponentCallback;

    public readonly currentSecurity$: Observable<CodeAndExchange | null>;

    public readonly newsSecurities$: Observable<Securities | null>;

    public readonly selectedSources$: Observable<SelectedNewsSources>;

    public readonly placeholder$: Observable<string>;

    private readonly configChanges$ = new Subject<NewsWidgetConfigChange>();

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    public constructor() {
        this.currentSecurity$ = this.buildCurrentSecurity();
        this.newsSecurities$ = this.buildNewsSecurities();
        this.selectedSources$ = this.buildSelectedNewsSources();
        this.placeholder$ = this.buildPlaceholder();
    }

    private buildCurrentSecurity(): Observable<CodeAndExchange | null> {
        return this.config$
                   .takeUntil(this.destroyed$)
                   .filter(isNotNull)
                   .map((c) => c.selected!)
                   .startWith(null)
                   .publishReplay(1)
                   .refCount();
    }

    private buildNewsSecurities(): Observable<Securities | null> {
        return this.currentSecurity$
                   .map((c) => (c == null) ? null : [c])
                   .publishReplay(1)
                   .refCount();
    }

    private buildSelectedNewsSources(): Observable<SelectedNewsSources> {
        return this.config$
                   .filter(isNotNull)
                   .map((c) => c.sources)
                   .filter(isNotNull)
                   .publishReplay(1)
                   .refCount();
    }

    private buildPlaceholder(): Observable<string> {
        return this.currentSecurity$
                   .map((sec) => (sec == null) ? "All Securities" : sec.code)
                   .publishReplay(1)
                   .refCount();
    }

    public $onInit(): void {
        this.watchChanges();
    }

    private watchChanges() {
        this.configChanges$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.config$)
            .map(([change, config]) => this.updateConfig(change, config))
            .subscribe((config) => this.onConfigChanged({config}));
    }

    private updateConfig(change: NewsWidgetConfigChange, config: NewsWidgetConfig | null): NewsWidgetConfig {
        if (config == null) {
            config = {};
        }
        if (isSecurityChange(change)) {
            return {
                ...config,
                selected: change.security
            };
        } else if (isSourcesChange(change)) {
            return {
                ...config,
                sources: change.sources
            };
        } else {
            return config;
        }
    }

    private configUpdated(config: NewsWidgetConfig) {
        this.onConfigChanged({config});
    }

    public securityChanged(security: CodeAndExchange) {
        const change = SecurityChange(security);
        this.configChanges$.next(change);
        this.onSecurityChanged({security});
    }

    public selectedSourcesChanged(selected: SelectedNewsSources) {
        const change = SourcesChange(selected);
        this.configChanges$.next(change);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
